import { GenericFieldArray } from "@/lib/Components/Form/FieldArray/GenericFieldArray";
import { NumberInput } from "@/lib/Components/Form/Inputs/NumberInput";
import {
  BookingChargeInput,
  BookingChargeType,
  UpdateBookingInput,
} from "@/gql/graphql";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { useBooking } from "@/app/Bookings/Hooks/useBooking";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { BookingChargeTypeInput } from "@/app/Bookings/Forms/BookingChargeTypeInput";
import { Button } from "@/components/catalyst/button";
import { useFormikContext } from "formik";
import { BookingRecord } from "@/app/Bookings/GraphQL/bookingRecordQuery";
import { PlusIcon } from "@heroicons/react/20/solid";
import { InfoBanner } from "@/lib/Components/Banners/InfoBanner";

export function BookingChargeForm({ bookingId }: { bookingId: string }) {
  const booking = useBooking(bookingId);
  const { values } = useFormikContext<UpdateBookingInput>();

  const charges = values.charges;
  const hasBookingFee = charges?.upsert?.find(
    (charge) => charge.type === BookingChargeType.BookingFee,
  );
  const hasDeposit = charges?.upsert?.some((charge) =>
    [
      BookingChargeType.SupplierRefundDeposit,
      BookingChargeType.PlatformHeldDeposit,
    ].includes(charge.type),
  );

  return (
    <GenericFieldArray<BookingChargeInput>
      className="grid-cols-5"
      fieldArrayKey="charges.upsert"
      lineItemLabel="Charge"
      newLineItemValues={{
        platform_unit_price_collectable: booking.relocation.booking_fee_amount,
        supplier_unit_price_collectable: 0,
        type: BookingChargeType.BookingFee,
        quantity: 1,
      }}
      footer={<Footer booking={booking} />}
      isDeletable={(item) => {
        return [
          BookingChargeType.BookingFee,
          BookingChargeType.SupplierRefundDeposit,
          BookingChargeType.PlatformHeldDeposit,
        ].includes(item.type);
      }}
      addLineItemButton={({ fieldHelpers }) => {
        return (
          <div className="space-x-2">
            <Button
              outline
              disabled={!!hasBookingFee}
              LeadingIcon={PlusIcon}
              onClick={() => {
                fieldHelpers.push({
                  platform_unit_price_collectable:
                    booking.relocation.booking_fee_amount,
                  type: BookingChargeType.BookingFee,
                  quantity: 1,
                } satisfies BookingChargeInput);
              }}
            >
              Booking Fee
            </Button>
            <Button
              outline
              disabled={hasDeposit}
              LeadingIcon={PlusIcon}
              onClick={() => {
                fieldHelpers.push({
                  platform_unit_price_collectable:
                    booking.relocation.holding_deposit_amount,
                  type: BookingChargeType.PlatformHeldDeposit,
                  quantity: 1,
                } satisfies BookingChargeInput);
              }}
            >
              Platform Deposit
            </Button>
            <Button
              outline
              disabled={hasDeposit}
              LeadingIcon={PlusIcon}
              onClick={() => {
                fieldHelpers.push({
                  platform_unit_price_collectable:
                    booking.relocation.holding_deposit_amount,
                  type: BookingChargeType.SupplierRefundDeposit,
                  quantity: 1,
                } satisfies BookingChargeInput);
              }}
            >
              Supplier Refund Deposit
            </Button>
          </div>
        );
      }}
    >
      {(object, idx) => {
        const supplierUnitPrice = object.supplier_unit_price_collectable ?? 0;
        const platformUnitPrice = object.platform_unit_price_collectable ?? 0;

        return (
          <>
            {object.type === BookingChargeType.AdditionalCharge ? (
              <InfoBanner className="col-span-full">
                This charge is configured by the inclusions on the booking
              </InfoBanner>
            ) : null}

            {object.type === BookingChargeType.PurchasableExtra ? (
              <InfoBanner className="col-span-full">
                This charge is configured by the extras on the booking
              </InfoBanner>
            ) : null}

            <BookingChargeTypeInput
              name={`charges.upsert.${idx}.type`}
              label="Type"
              className="col-span-1"
              isEditable={false}
            />

            <NumberInput
              name={`charges.upsert.${idx}.quantity`}
              label="Quantity"
              className="col-span-1"
              isEditable={false}
            />

            {[
              BookingChargeType.BookingFee,
              BookingChargeType.SupplierRefundDeposit,
              BookingChargeType.PlatformHeldDeposit,
              BookingChargeType.DailyRate,
            ].includes(object.type) ? (
              <CurrencyInput
                name={`charges.upsert.${idx}.platform_unit_price_collectable`}
                label="Unit Price (Imoova Net)"
                className="col-span-1"
                currency={booking.currency}
              />
            ) : (
              <div />
            )}

            {[
              BookingChargeType.RelocationRate,
              BookingChargeType.DailyRate,
              BookingChargeType.AdditionalCharge,
              BookingChargeType.PurchasableExtra,
            ].includes(object.type) ? (
              <CurrencyInput
                name={`charges.upsert.${idx}.supplier_unit_price_collectable`}
                label="Unit Price (Supplier Net)"
                tooltip={`The amount collectable by ${booking.supplier.name}`}
                className="col-span-1"
                currency={booking.currency}
                isEditable={[
                  BookingChargeType.DailyRate,
                  BookingChargeType.RelocationRate,
                ].includes(object.type)}
              />
            ) : (
              <div />
            )}

            <div className="flex flex-col justify-end">
              <div className="text-right font-mono text-gray-500">
                {formatCurrency(
                  (platformUnitPrice + supplierUnitPrice) * object.quantity,
                  booking.currency,
                )}
              </div>
            </div>
          </>
        );
      }}
    </GenericFieldArray>
  );
}

function Footer({ booking }: { booking: BookingRecord }) {
  const { values } = useFormikContext<UpdateBookingInput>();

  const total =
    values.charges?.upsert?.reduce((acc, charge) => {
      return (
        acc +
        (charge.supplier_unit_price_collectable ?? 0) * charge.quantity +
        (charge.platform_unit_price_collectable ?? 0) * charge.quantity
      );
    }, 0) ?? 0;

  const imoovaTotal =
    values.charges?.upsert?.reduce((acc, charge) => {
      return (
        acc + (charge.platform_unit_price_collectable ?? 0) * charge.quantity
      );
    }, 0) ?? 0;

  const supplierTotal = total - imoovaTotal;

  return (
    <table className="min-w-full divide-y divide-gray-300">
      <tfoot>
        <tr>
          <th
            scope="row"
            className="hidden pl-6 pr-3 pt-4 text-right text-sm font-normal text-gray-500 sm:table-cell md:pl-0"
          >
            Collectable By Imoova
          </th>

          <td className="w-36 pl-3 pr-4 pt-6 text-right text-sm text-gray-500 sm:pr-6 md:pr-0">
            {formatCurrency(imoovaTotal, booking.currency)}
          </td>
        </tr>
        <tr>
          <th
            scope="row"
            className="hidden pl-6 pr-3 pt-4 text-right text-sm font-semibold text-gray-900 sm:table-cell md:pl-0"
          >
            Collectable By Supplier
          </th>

          <td className="pl-3 pr-4 pt-4 text-right text-sm font-semibold text-gray-900 sm:pr-6 md:pr-0">
            {formatCurrency(supplierTotal, booking.currency)}
          </td>
        </tr>
      </tfoot>
    </table>
  );
}
